import { Icon } from '@hvac/ui';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { stashDroppedFiles } from '../../shared/upload/drop-buffer.js';

/**
 * The whole app is the drop target.
 *
 * "Don't think about where to put it" has to be true at the muscle-memory
 * level: a file dragged over ANY screen dims the app, names the promise, and
 * a drop routes it into intake. The Documents page keeps its own dropzone,
 * when a drop lands there first and calls preventDefault, this layer sees
 * `defaultPrevented` and stands down rather than double-adding.
 */
export function GlobalDropzone() {
  const [active, setActive] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // dragenter/dragleave fire for every child crossed; a depth counter is the
    // classic cure for the flicker.
    let depth = 0;

    const hasFiles = (event: DragEvent): boolean =>
      Array.from(event.dataTransfer?.types ?? []).includes('Files');
    // A page whose whole point is one particular file (the spreadsheet
    // import) owns its drops: no overlay promising to sort it elsewhere.
    const pageOwnsDrops = () => document.body.dataset['ownDrops'] === 'true';

    const onDragEnter = (event: DragEvent) => {
      if (!hasFiles(event) || pageOwnsDrops()) return;
      depth += 1;
      setActive(true);
    };
    const onDragLeave = () => {
      depth = Math.max(0, depth - 1);
      if (depth === 0) setActive(false);
    };
    const onDragOver = (event: DragEvent) => {
      // Without this the browser navigates away to the file. Non-negotiable.
      if (hasFiles(event)) event.preventDefault();
    };
    const onDrop = (event: DragEvent) => {
      depth = 0;
      setActive(false);
      if (!hasFiles(event) || event.defaultPrevented) return;
      event.preventDefault();
      if (pageOwnsDrops()) return;
      const files = Array.from(event.dataTransfer?.files ?? []);
      if (files.length === 0) return;
      stashDroppedFiles(files);
      void navigate({ to: '/documents' });
    };

    window.addEventListener('dragenter', onDragEnter);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragenter', onDragEnter);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('drop', onDrop);
    };
  }, [navigate]);

  if (!active) return null;

  return (
    <div
      aria-hidden="true"
      className="animate-overlay-in pointer-events-none fixed inset-0 z-40 flex items-center justify-center bg-sidebar/40 backdrop-blur-sm"
    >
      <div className="animate-card-pop relative rounded-[var(--radius-overlay)] bg-surface px-14 py-12 text-center shadow-[var(--shadow-overlay)]">
        {/* Marching ants trace the drop target, the border itself says "here". */}
        <svg className="pointer-events-none absolute inset-2" aria-hidden="true">
          <rect
            x="1"
            y="1"
            width="calc(100% - 2px)"
            height="calc(100% - 2px)"
            rx="14"
            fill="none"
            stroke="var(--color-brand-400)"
            strokeWidth="2"
            className="animate-ants"
          />
        </svg>

        <span className="animate-bob mx-auto flex size-16 items-center justify-center rounded-full bg-brand-50">
          <Icon name="drop" className="size-8 text-brand-600" />
        </span>
        <p className="mt-5 text-2xl font-bold tracking-tight text-content">Drop it in.</p>
        <p className="mx-auto mt-2 max-w-xs text-sm leading-relaxed text-content-muted">
          Receipts, invoices, photos, zips, emails, bank files, spreadsheets, the system reads the
          bytes and sorts out what it is.
        </p>
      </div>
    </div>
  );
}
